import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { insertItem, patch, removeItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { UserOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { OrderStatus } from '../enums/order-status.enum';
import { IOrderResponse } from '../models/order.model';

import { OrderApiService } from '../services/order-api.service';
import {
  ApproveOrder,
  CancelOrder,
  CreateOrder,
  DeleteOrder,
  LoadApprovedOrders,
  LoadOrderById,
  LoadOrdersSentToSuppliers,
  LoadPagedOrders,
  PrintOrderExcel,
  PrintOrderPdf,
  PrintOrderReport,
  ReceiveOrder,
  ReopenOrder,
  SendOrderForApproval,
  SendOrderToDraft,
  SendOrderToSupplier,
  UpdateOrder,
  ClearCurrentOrder,
} from './order.actions';
import { getLinkToOrderStage } from '../functions/get-link-to-order-stage';

export interface OrderStateModel {
  ordersLoaded: boolean;
  orders: IOrderResponse[];
  ordersSentToSuppliers: IOrderResponse[];
  approvedOrders: IOrderResponse[];
  allOrders: IdName[];
  ordersTotalPages: number;
  currentOrder: IOrderResponse;
  currentOrderPdf: Blob;
  currentOrderExcel: Blob;
  orderReport: Blob;
}

const ORDER_STATE_TOKEN = new StateToken<OrderStateModel>('orders');

@State<OrderStateModel>({
  name: ORDER_STATE_TOKEN,
  defaults: {
    ordersLoaded: false,
    orders: [],
    ordersSentToSuppliers: [],
    approvedOrders: [],
    allOrders: [],
    ordersTotalPages: 0,
    currentOrder: <IOrderResponse>{},
    currentOrderPdf: <Blob>{},
    currentOrderExcel: <Blob>{},
    orderReport: <Blob>{},
  },
})
@Injectable()
export class OrderState {
  constructor(
    private service: OrderApiService,
    private store: Store
  ) {}

  @Action(LoadPagedOrders, { cancelUncompleted: true })
  loadOrders(
    { getState, setState }: StateContext<OrderStateModel>,
    { orderStage, filter }: LoadPagedOrders
  ) {
    const allDeps = getDepartmentId() === ALL_DEPS_TOKEN;
    return this.service.getOrders(orderStage, filter, allDeps).pipe(
      tap((response: ApiResponse<IOrderResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          ordersLoaded: true,
          orders: response.data,
          ordersTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadOrdersSentToSuppliers, { cancelUncompleted: true })
  loadOrdersSentToSuppliers(
    { getState, setState }: StateContext<OrderStateModel>,
    { filter }: LoadOrdersSentToSuppliers
  ) {
    const allDeps =
      this.store.selectSnapshot(TenantUserAccessState.currentDepartmentId) === ALL_DEPS_TOKEN;
    return this.service.getOrders(OrderStatus.SentToSupplier, filter, allDeps).pipe(
      tap((response: ApiResponse<IOrderResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          ordersSentToSuppliers: response.data,
        });
      })
    );
  }

  @Action(LoadApprovedOrders, { cancelUncompleted: true })
  loadApprovedOrders(
    { getState, setState }: StateContext<OrderStateModel>,
    { filter }: LoadApprovedOrders
  ) {
    const allDeps =
      this.store.selectSnapshot(TenantUserAccessState.currentDepartmentId) === ALL_DEPS_TOKEN;
    return this.service.getOrders(OrderStatus.Approved, filter, allDeps).pipe(
      tap((response: ApiResponse<IOrderResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          approvedOrders: response.data,
        });
      })
    );
  }

  @Action(LoadOrderById, { cancelUncompleted: true })
  loadOrderById({ getState, setState }: StateContext<OrderStateModel>, { id }: LoadOrderById) {
    return this.service.getOrderById(id).pipe(
      tap((response: IOrderResponse) => {
        const state = getState();
        setState({
          ...state,
          ordersLoaded: true,
          currentOrder: response,
        });
      })
    );
  }

  @Action(CreateOrder, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Dashboard, message: 'orderCreated' })
  createOrder(ctx: StateContext<OrderStateModel>, { order }: CreateOrder) {
    return this.service.createOrder(order).pipe(
      tap((data: IOrderResponse) => {
        ctx.setState(
          patch({
            orders: insertItem<IOrderResponse>(data),
          })
        );
      })
    );
  }

  @Action(UpdateOrder, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Dashboard, message: 'orderUpdated' })
  updateOrder(ctx: StateContext<OrderStateModel>, { order }: UpdateOrder) {
    return this.service.updateOrder(order);
  }

  @Action(DeleteOrder, { cancelUncompleted: true })
  @OnSuccess({ message: 'orderDeleted', dispatch: ResetCurrentFilter })
  deleteOrder(ctx: StateContext<OrderStateModel>, { orderId }: DeleteOrder) {
    return this.service.deleteOrder(orderId).pipe(
      tap((response: IOrderResponse) => {
        ctx.setState(
          patch({
            orders: removeItem<IOrderResponse>(
              (orderToRemove: IOrderResponse | undefined) => orderToRemove?.id === orderId
            ),
          })
        );
        redirectAndBypassUnsavedDataCheck(
          getLinkToOrderStage(ctx.getState().currentOrder.orderStage)
        );
      })
    );
  }

  @Action(PrintOrderPdf, { cancelUncompleted: true })
  printOrderPdf({ getState, setState }: StateContext<OrderStateModel>, { orderId }: PrintOrderPdf) {
    return this.service.printOrderPdf(orderId).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          currentOrderPdf: file,
        });
      })
    );
  }

  @Action(PrintOrderExcel, { cancelUncompleted: true })
  printOrderExcel(
    { getState, setState }: StateContext<OrderStateModel>,
    { orderId }: PrintOrderExcel
  ) {
    return this.service.printOrderExcel(orderId).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          currentOrderExcel: file,
        });
      })
    );
  }

  @Action(SendOrderToDraft, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Dashboard, message: 'orderSentToDraft' })
  sendOrderToDraft(ctx: StateContext<OrderStateModel>, { orderId }: SendOrderToDraft) {
    return this.service.sendOrderToDraft(orderId);
  }

  @Action(SendOrderForApproval, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Dashboard, message: 'orderSentForApproval' })
  sendOrderForApproval(ctx: StateContext<OrderStateModel>, { orderId }: SendOrderForApproval) {
    return this.service.sendOrderForApproval(orderId);
  }

  @Action(ApproveOrder, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Dashboard, message: 'orderApproved' })
  approveOrder(ctx: StateContext<OrderStateModel>, { orderId }: ApproveOrder) {
    return this.service.approveOrder(orderId);
  }

  @Action(SendOrderToSupplier, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Dashboard, message: 'orderSentToSupplier' })
  sendOrderToSupplier(
    ctx: StateContext<OrderStateModel>,
    { orderId, sendEmailManually, addPurchaserContactInCC }: SendOrderToSupplier
  ) {
    return this.service.sendOrderToSupplier(orderId, sendEmailManually, addPurchaserContactInCC);
  }

  @Action(ReceiveOrder, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Dashboard, message: 'orderMarkedAsReceived' })
  receiveOrder(ctx: StateContext<OrderStateModel>, { orderId, receivedOrderLines }: ReceiveOrder) {
    return this.service.receiveOrder(orderId, receivedOrderLines);
  }

  @Action(CancelOrder, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Dashboard, message: 'orderCancelled' })
  cancelOrder(ctx: StateContext<OrderStateModel>, { orderId }: CancelOrder) {
    return this.service.cancelOrder(orderId);
  }

  @Action(ReopenOrder, { cancelUncompleted: true })
  @OnSuccess({ url: UserOrderRoutes.Cancelled, message: 'orderReopened' })
  reopenOrder(ctx: StateContext<OrderStateModel>, { orderId }: ReopenOrder) {
    return this.service.reopenOrder(orderId);
  }

  @Action(PrintOrderReport, { cancelUncompleted: true })
  printOrderReport(
    { getState, setState }: StateContext<OrderStateModel>,
    { filter, menuId }: PrintOrderReport
  ) {
    return this.service.printReport(filter, menuId).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          orderReport: file,
        });
      })
    );
  }

  @Action(ClearCurrentOrder)
  clearCurrentOrder(ctx: StateContext<OrderStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentOrder: <IOrderResponse>{},
    });
  }

  @Selector()
  static ordersLoaded(state: OrderStateModel) {
    return state.ordersLoaded;
  }

  @Selector()
  static getOrdersTotalPages(state: OrderStateModel) {
    return state.ordersTotalPages;
  }

  @Selector()
  static getOrders(state: OrderStateModel): IOrderResponse[] {
    return state.orders;
  }

  @Selector()
  static getOrdersSentToSuppliers(state: OrderStateModel): IOrderResponse[] {
    return state.ordersSentToSuppliers;
  }

  @Selector()
  static getApprovedOrders(state: OrderStateModel): IOrderResponse[] {
    return state.approvedOrders;
  }

  @Selector()
  static getAllOrders(state: OrderStateModel): IdName[] {
    return state.allOrders;
  }

  @Selector()
  static getOrderById(state: OrderStateModel) {
    return state.currentOrder;
  }

  @Selector()
  static getOrderAsPdf(state: OrderStateModel) {
    return state.currentOrderPdf;
  }

  @Selector()
  static getOrderAsExcel(state: OrderStateModel) {
    return state.currentOrderExcel;
  }

  @Selector()
  static getOrderReport(state: OrderStateModel) {
    return state.orderReport;
  }
}
